/**
 * User-safe checkout error copy. Backend returns stable `data.code` values on `/api/checkout/*`
 * validation failures; map those here before surfacing in segment `error` fields or toasts.
 */

export const CHECKOUT_ERROR_CODES = {
    cartIdRequired: 'cart_id_required',
    handwritingStationeryNotFound: 'handwriting_stationery_not_found',
    handwritingStationeryQuantityRequired: 'handwriting_stationery_quantity_required',
    designProjectNotFound: 'design_project_not_found',
    designProjectAlreadyOrdered: 'design_project_already_ordered',
    designProjectOrderStateRequired: 'design_project_order_state_required',
    paymentMethodRequiredPaid: 'payment_method_required_paid',
    paymentMethodInvalidObject: 'payment_method_invalid_object',
    paymentMethodCodeRequired: 'payment_method_code_required',
    paymentMethodAdditionalDataInvalid: 'payment_method_additional_data_invalid',
    placeCartJourneyUnsupported: 'place_cart_journey_unsupported',
    fetchCheckoutDetailsFailed: 'fetch_checkout_details_failed',
    checkoutDetailsCartMissing: 'checkout_details_cart_missing',
    shippingAddressRequired: 'shipping_address_required',
    proSubscriptionRequired: 'pro_subscription_required',
    handwritingVoucherAlreadyConsumed: 'handwriting_voucher_already_consumed',
    placeCartInternalError: 'place_cart_internal_error',
    cartQuantityMismatch: 'cart_quantity_mismatch',
    handwritingProjectNotFound: 'handwriting_project_not_found',
    handwritingProjectNotSubmitted: 'handwriting_project_not_submitted',
    handwritingProjectAlreadyProcessing: 'handwriting_project_already_processing',
    noteCreditRequired: 'note_credit_required',
    skuRequired: 'sku_required',
    skuMismatch: 'sku_mismatch',
} as const

export type CheckoutErrorCode = (typeof CHECKOUT_ERROR_CODES)[keyof typeof CHECKOUT_ERROR_CODES]

/** Stable checkout error code → user-facing message (also used as H3 `statusMessage`). */
export const CHECKOUT_ERROR_CODE_MAP: Record<CheckoutErrorCode, string> = {
    [CHECKOUT_ERROR_CODES.cartIdRequired]: 'cart_id is required — run prepare-cart first',
    [CHECKOUT_ERROR_CODES.handwritingStationeryNotFound]: 'handwriting_stationery not found',
    [CHECKOUT_ERROR_CODES.handwritingStationeryQuantityRequired]:
        'handwriting stationery quantity is required before place',
    [CHECKOUT_ERROR_CODES.designProjectNotFound]: 'design_project not found',
    [CHECKOUT_ERROR_CODES.designProjectAlreadyOrdered]: 'design_project is already ordered',
    [CHECKOUT_ERROR_CODES.designProjectOrderStateRequired]:
        'design_project order_state is required before place',
    [CHECKOUT_ERROR_CODES.paymentMethodRequiredPaid]:
        'payment_method.code is required for paid checkout',
    [CHECKOUT_ERROR_CODES.paymentMethodInvalidObject]: 'payment_method must be an object',
    [CHECKOUT_ERROR_CODES.paymentMethodCodeRequired]: 'payment_method.code is required',
    [CHECKOUT_ERROR_CODES.paymentMethodAdditionalDataInvalid]:
        'payment_method.additional_data must be an array of { key, value } strings',
    [CHECKOUT_ERROR_CODES.placeCartJourneyUnsupported]:
        'POST /api/checkout/place-cart supports handwriting_stationery (paid/voucher) and handwritten_note (paid/async_credit)',
    [CHECKOUT_ERROR_CODES.fetchCheckoutDetailsFailed]:
        "Sorry, we're having trouble loading your checkout details. Please try again.",
    [CHECKOUT_ERROR_CODES.checkoutDetailsCartMissing]:
        "Sorry, we couldn't load your checkout details. Please try again.",
    [CHECKOUT_ERROR_CODES.shippingAddressRequired]:
        'Shipping address is required before placing your order. Please return to checkout.',
    [CHECKOUT_ERROR_CODES.proSubscriptionRequired]: 'Pro subscription required',
    [CHECKOUT_ERROR_CODES.handwritingVoucherAlreadyConsumed]:
        'Handwriting voucher already consumed',
    [CHECKOUT_ERROR_CODES.placeCartInternalError]: 'Internal server error while placing cart',
    [CHECKOUT_ERROR_CODES.cartQuantityMismatch]:
        'We could not verify your cart quantity. Please return to checkout and try again.',
    [CHECKOUT_ERROR_CODES.handwritingProjectNotFound]: 'handwriting_project not found',
    [CHECKOUT_ERROR_CODES.handwritingProjectNotSubmitted]:
        'handwriting_project must be submitted before it can be placed',
    [CHECKOUT_ERROR_CODES.handwritingProjectAlreadyProcessing]:
        'handwriting_project is already in production or cancelled',
    [CHECKOUT_ERROR_CODES.noteCreditRequired]: 'No available note credit for this account',
    [CHECKOUT_ERROR_CODES.skuRequired]: 'sku is required',
    [CHECKOUT_ERROR_CODES.skuMismatch]: 'sku does not match the configured product SKU',
}

export const CHECKOUT_ERROR_HTTP_STATUS: Record<CheckoutErrorCode, number> = {
    [CHECKOUT_ERROR_CODES.cartIdRequired]: 400,
    [CHECKOUT_ERROR_CODES.handwritingStationeryNotFound]: 400,
    [CHECKOUT_ERROR_CODES.handwritingStationeryQuantityRequired]: 400,
    [CHECKOUT_ERROR_CODES.designProjectNotFound]: 404,
    [CHECKOUT_ERROR_CODES.designProjectAlreadyOrdered]: 409,
    [CHECKOUT_ERROR_CODES.designProjectOrderStateRequired]: 400,
    [CHECKOUT_ERROR_CODES.paymentMethodRequiredPaid]: 400,
    [CHECKOUT_ERROR_CODES.paymentMethodInvalidObject]: 400,
    [CHECKOUT_ERROR_CODES.paymentMethodCodeRequired]: 400,
    [CHECKOUT_ERROR_CODES.paymentMethodAdditionalDataInvalid]: 400,
    [CHECKOUT_ERROR_CODES.placeCartJourneyUnsupported]: 400,
    [CHECKOUT_ERROR_CODES.fetchCheckoutDetailsFailed]: 500,
    [CHECKOUT_ERROR_CODES.checkoutDetailsCartMissing]: 500,
    [CHECKOUT_ERROR_CODES.shippingAddressRequired]: 400,
    [CHECKOUT_ERROR_CODES.proSubscriptionRequired]: 403,
    [CHECKOUT_ERROR_CODES.handwritingVoucherAlreadyConsumed]: 409,
    [CHECKOUT_ERROR_CODES.placeCartInternalError]: 500,
    [CHECKOUT_ERROR_CODES.cartQuantityMismatch]: 400,
    [CHECKOUT_ERROR_CODES.handwritingProjectNotFound]: 404,
    [CHECKOUT_ERROR_CODES.handwritingProjectNotSubmitted]: 400,
    [CHECKOUT_ERROR_CODES.handwritingProjectAlreadyProcessing]: 409,
    [CHECKOUT_ERROR_CODES.noteCreditRequired]: 403,
    [CHECKOUT_ERROR_CODES.skuRequired]: 400,
    [CHECKOUT_ERROR_CODES.skuMismatch]: 400,
}

/**
 * Customer-safe copy for a subset of {@link CHECKOUT_ERROR_CODES} — deliberately not every code.
 * `CHECKOUT_ERROR_CODE_MAP` doubles as the H3 `statusMessage` (developer/log-facing, e.g. field
 * names, route descriptions) and must never be shown to a customer directly. Codes without an
 * entry here (internal/validation errors not actionable by the customer, e.g.
 * `placeCartJourneyUnsupported`, `cartIdRequired`) fall through to the caller's generic fallback.
 */
const CHECKOUT_ERROR_CODE_USER_MESSAGE: Partial<Record<CheckoutErrorCode, string>> = {
    [CHECKOUT_ERROR_CODES.fetchCheckoutDetailsFailed]:
        CHECKOUT_ERROR_CODE_MAP[CHECKOUT_ERROR_CODES.fetchCheckoutDetailsFailed],
    [CHECKOUT_ERROR_CODES.checkoutDetailsCartMissing]:
        CHECKOUT_ERROR_CODE_MAP[CHECKOUT_ERROR_CODES.checkoutDetailsCartMissing],
    [CHECKOUT_ERROR_CODES.shippingAddressRequired]:
        CHECKOUT_ERROR_CODE_MAP[CHECKOUT_ERROR_CODES.shippingAddressRequired],
    [CHECKOUT_ERROR_CODES.cartQuantityMismatch]:
        CHECKOUT_ERROR_CODE_MAP[CHECKOUT_ERROR_CODES.cartQuantityMismatch],
    [CHECKOUT_ERROR_CODES.skuMismatch]:
        'We could not verify your order details. Please return to checkout and try again.',
    [CHECKOUT_ERROR_CODES.proSubscriptionRequired]:
        'A Pro subscription is required for this order.',
    [CHECKOUT_ERROR_CODES.handwritingVoucherAlreadyConsumed]: 'This voucher has already been used.',
    [CHECKOUT_ERROR_CODES.designProjectAlreadyOrdered]: 'This design has already been ordered.',
    [CHECKOUT_ERROR_CODES.handwritingProjectAlreadyProcessing]:
        'This note is already being processed.',
    [CHECKOUT_ERROR_CODES.noteCreditRequired]: "You don't have an available credit for this note.",
}

export const CHECKOUT_USER_ERROR_MESSAGES = {
    generic: 'Checkout is unavailable. Please try again.',
    journeyNotBound: 'Checkout is unavailable. Please try again.',
    /** Shared with `createGuestCart` atomic — keep in sync for store segment + API errors. */
    prepareCart: "Sorry, we're having trouble with your cart. Please try again.",
    checkCredits: 'We could not verify your credits. Please try again.',
    finalizeCart: 'We could not load your order details. Please try again.',
    placeOrder: 'We could not complete your order. Please try again.',
    /**
     * Stripe `confirmPayment` already succeeded; `place-cart` failed. Retry reuses the same
     * `client_secret` without charging again (TK-12335).
     */
    placeOrderAfterPayment:
        'Your payment went through, but we could not finish your order. Tap Pay again — you will not be charged twice.',
    applyCoupon: 'We could not apply your coupon. Please try again.',
    removeCoupon: 'We could not remove your coupon. Please try again.',
    syncReturnAddress: 'We could not update your return address on this order. Please try again.',
    persistStripeBillingAddress:
        'We could not save your billing address to your profile. Please try again.',
    /**
     * Async credit: optimistic personalize seed failed and there is no client prepare/finalize
     * (worker owns ACCS cart). Display-only — Send Note Now must remain available.
     */
    addressPreviewUnavailable:
        'We could not preview your addresses. You can still send — we will use the addresses from your note.',
    getPaymentIntent: 'We could not prepare your payment. Please try again.',
    confirmPayment: "We couldn't confirm your payment. Please check your details and try again.",
    /** Guest `submitCheckout` without email on hydrate (integration / legacy). */
    checkoutEmailRequired: 'Please enter your email to continue checkout.',
    /** Credit checkout submit after live re-check finds no note credit — pivot to paid UI. */
    noteCreditNoLongerAvailable:
        'Your Pro credit is no longer available. Complete payment below to send your note.',
} as const

export function checkoutErrorMessage(code: CheckoutErrorCode): string {
    return CHECKOUT_ERROR_CODE_MAP[code]
}

function readCheckoutErrorCode(error: unknown): CheckoutErrorCode | undefined {
    if (!error || typeof error !== 'object') {
        return undefined
    }

    const candidates: unknown[] = [
        (error as { data?: { code?: unknown } }).data?.code,
        (error as { code?: unknown }).code,
    ]

    for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate in CHECKOUT_ERROR_CODE_MAP) {
            return candidate as CheckoutErrorCode
        }
    }

    return undefined
}

/**
 * Reads user-safe copy from a checkout API error code, or falls back to the caller's generic
 * message. Never returns a raw `error.message` or the technical `CHECKOUT_ERROR_CODE_MAP` text —
 * both can contain developer-facing details (field names, route descriptions, Stripe/ACCS
 * internals) that must not reach the customer.
 */
export function toCheckoutUserMessage(
    error: unknown,
    fallback: string = CHECKOUT_USER_ERROR_MESSAGES.generic
): string {
    const code = readCheckoutErrorCode(error)
    if (code && code in CHECKOUT_ERROR_CODE_USER_MESSAGE) {
        return CHECKOUT_ERROR_CODE_USER_MESSAGE[code]!
    }

    return fallback
}
